import React, { forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  type?: 'success' | 'error' | 'info' | 'warning';
}

interface ToastProps extends Toast {
  onClose: () => void;
}

const toastVariants = {
  initial: { opacity: 0, y: 50, scale: 0.3 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, scale: 0.5, transition: { duration: 0.2 } }
};

const toastStyles = {
  success: 'bg-green-500/10 border-green-500/50 text-green-400',
  error: 'bg-red-500/10 border-red-500/50 text-red-400',
  info: 'bg-blue-500/10 border-blue-500/50 text-blue-400',
  warning: 'bg-yellow-500/10 border-yellow-500/50 text-yellow-400'
};

const ToastComponent = forwardRef<HTMLDivElement, ToastProps>(
  ({ id, title, description, type = 'info', onClose }, ref) => {
    return (
      <motion.div
        ref={ref}
        layout
        variants={toastVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn(
          'relative overflow-hidden rounded-lg border p-4 pr-8 shadow-lg backdrop-blur-sm',
          toastStyles[type]
        )}
      >
        <button
          onClick={onClose}
          className="absolute right-1 top-1 p-1 rounded-lg opacity-70 hover:opacity-100 transition-opacity"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex gap-3">
          <div className="flex-1">
            <h3 className="font-medium mb-1">{title}</h3>
            {description && (
              <p className="text-sm opacity-90">{description}</p>
            )}
          </div>
        </div>

        <motion.div
          className="absolute bottom-0 left-0 right-0 h-1 bg-current opacity-20"
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: 5 }}
          onAnimationComplete={onClose}
        />
      </motion.div>
    );
  }
);

ToastComponent.displayName = 'Toast';

export const Toast = motion(ToastComponent);

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md w-full">
      <AnimatePresence mode="popLayout">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={() => onRemove(toast.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}